import * as d3 from 'd3'
import * as dc from 'dc'
import { HOUR_BUCKETS, hourIndexFromHour } from '@/utils/flowSightHourBuckets'

// "Clients by Maturity Range" row chart (Overview tab) - X axis is the
// DISTINCT client count, Y axis the maturity buckets below.
//

// Counting uses the same reference-counted Map reduce as
// createClientsVolumeChart: a plain Set would collapse a whole bucket to 0 as
// soon as another chart's filter excluded the FIRST row of a counterparty that
// still has other active rows in that bucket. Increment on add, delete only
// when the count reaches 0.
export function createClientsHourChart ({ el, ndx, chartGroup, width, color, t }) {
  const dim = ndx.dimension((d) => hourIndexFromHour(d.hourOfDay))
  const group = dim.group().reduce(
    (p, v) => {
      const key = v.counterparty
      p.set(key, (p.get(key) || 0) + 1)
      return p
    },
    (p, v) => {
      const key = v.counterparty
      const count = p.get(key)
      if (count <= 1) p.delete(key)
      else p.set(key, count - 1)
      return p
    },
    () => new Map()
  )

  // Unwraps each Map to its .size (row charts need numbers) and pads the
  // buckets crossfilter has no rows for, so the maturity ladder keeps all its
  // rungs instead of resizing every time a filter empties one.
  const groupCount = {
    all: () => {
      const map = new Map(group.all().map((d) => [d.key, d.value.size]))
      return HOUR_BUCKETS.map((_, i) => ({ key: i, value: map.get(i) || 0 }))
    },
  }

  const chart = new dc.RowChart(el, chartGroup)
  chart
    .width(width)
    // 225, matching "Clients by Notional Range" above it: same 5 buckets, same
    // height, same bar thickness. The pair has to keep summing to 450 to stay
    // level with the left column - see that chart for the arithmetic.
    .height(225)
    .x(d3.scaleLinear().domain([0, d3.max(groupCount.all(), (d) => d.value)]))
    .margins({ top: 0, right: 0, bottom: 50, left: 10 })
    .elasticX(true)
    .dimension(dim)
    .group(groupCount)
    .ordering((kv) => -kv.key) // chronological: 1-30D at the bottom, matching Conversions' split chart
    .controlsUseVisibility(true)
    .colors(color)
    .renderLabel(true)
    .label((d) => {
      const total = d3.sum(groupCount.all(), (kv) => kv.value)
      const percent = total > 0 ? (d.value / total) * 100 : 0
      return `${HOUR_BUCKETS[d.key].label} (${percent.toFixed(0)}%)`
    })
    .title((d) => `${HOUR_BUCKETS[d.key].label}: ${d.value}`)
    // dc's RowChart has no built-in x-gridline option (unlike BarChart's
    // renderVerticalGridLines), so this hand-draws one via the chart's own
    // x-scale ticks on every transition.
    .on('pretransition', (c) => {
      const body = c.select('g.chart-body')
      const x = c.x()
      const h = c.effectiveHeight()
      const ticks = x.ticks(20)
      const lines = body.selectAll('line.grid-y').data(ticks)
      lines
        .enter()
        .append('line')
        .attr('class', 'grid-y')
        .merge(lines)
        .attr('x1', (d) => x(d))
        .attr('x2', (d) => x(d))
        .attr('y1', 0)
        .attr('y2', h)
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1)
        .attr('opacity', 0.2)
      lines.exit().remove()
    })

  // dc's RowChart also has no built-in x-axis-label API (unlike BarChart's
  // .xAxisLabel()) - hand-appended here. Selected-then-appended rather than
  // appended outright, so redraws reposition the one label instead of stacking
  // a new <text> on top of it every cycle.
  chart.on('renderlet', (c) => {
    const svg = c.select('svg')
    let label = svg.select('text.x-axis-label')
    if (label.empty()) label = svg.append('text').attr('class', 'x-axis-label')
    label
      .attr('x', 75)
      .attr('y', c.height() - 10)
      .attr('text-anchor', 'middle')
      .text(t('flowSightOverview.clientCountAxis'))
  })

  chart.render()
  return chart
}
